import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit2, Trash2, X, Loader, Crown } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

function Modal({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40">
      <div className="absolute inset-0 bg-transparent" onClick={onClose} />
      <div className="relative bg-white shadow-sm w-full max-w-md z-10 overflow-hidden animate-in zoom-in-95 duration-200 rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">{title}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-[#D8DDE6] bg-[#F4F6F9] flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[12px] font-semibold text-[#706E6B] ">{label}</label>
      {children}
    </div>
  );
}

const BLANK_FORM = { name: '', defaultAssigneeId: '' };

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [deptRes, usersRes] = await Promise.all([
        api.get('/departments'),
        api.get('/auth/team-members'),
      ]);
      setDepartments(deptRes.data.departments || []);
      setUsers(usersRes.data.teamMembers || usersRes.data.users || []);
    } catch (err) {
      console.error('Departments fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingDept(null);
    setForm(BLANK_FORM);
    setShowModal(true);
  };

  const openEdit = (dept) => {
    setEditingDept(dept);
    setForm({ name: dept.name, defaultAssigneeId: dept.defaultAssigneeId?._id || dept.defaultAssigneeId || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), defaultAssigneeId: form.defaultAssigneeId || null };
      if (editingDept) {
        await api.put(`/departments/${editingDept._id}`, payload);
      } else {
        await api.post('/departments', payload);
      }
      alertService.toast('success', editingDept ? 'Department updated' : 'Department created');
      setShowModal(false);
      fetchData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dept) => {
    const confirmed = await alertService.confirm(
      'Delete Department?',
      `This removes "${dept.name}". Leads already routed to it keep their department reference.`,
      'warning',
      'Delete'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/departments/${dept._id}`);
      alertService.toast('success', 'Department deleted');
      fetchData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to delete department');
    }
  };

  const userLabel = (id) => {
    const u = users.find((u) => u._id === id);
    if (!u) return null;
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
  };

  return (
    <div className={PAGE}>
      <PageHeader
        title="Departments"
        icon={Building2}
        subtitle="Routing targets for the Workflow engine — assign incoming leads to the right team by default."
        actions={<button onClick={openCreate} className="h-8 px-3 bg-[#0176D3] text-white rounded whitespace-nowrap text-[13px] font-semibold hover:bg-[#014486] inline-flex items-center gap-2"><Plus size={14} /> New Department</button>}
      />

      {/* This is a distinct, narrower concept from Team Management's "Teams &
          Hierarchy" tab — a Department here is only ever a lead-routing label
          with one default assignee, not an org chart or a permissions group.
          For managing who reports to whom, or team-based visibility/RBAC,
          use Team Management instead. */}
      <div className="sf-card px-4 py-3 flex items-center gap-3 bg-[#F4F6F9] border-[#D8DDE6]">
        <Building2 size={16} className="text-[#706E6B] shrink-0" />
        <p className="text-[12px] text-[#706E6B] font-medium">
          Departments only route new leads to a default assignee — they aren't an org chart or a permissions group.
          For team membership, managers, and visibility rules, use{' '}
          <a href="/dashboard/crm/team" className="text-[#0176D3] font-bold hover:underline">Team Management</a>.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <Loader size={28} className="animate-spin text-[#0176D3]" />
        </div>
      ) : departments.length === 0 ? (
        <div className="sf-card text-center py-16 space-y-3">
          <Building2 size={44} className="mx-auto text-[#D8DDE6]" />
          <p className="text-[#080707] font-bold text-[13px]">No departments yet</p>
          <p className="text-[#706E6B] text-[12px]">
            Create a department (e.g. "Sales", "Support") so a Workflow can route new leads to it automatically.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.map((dept) => (
            <div key={dept._id} className="sf-card p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <p className="font-bold text-[#080707] text-[14px]">{dept.name}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => openEdit(dept)}
                    title="Edit Department"
                    className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:border-[#0176D3] hover:text-[#0176D3] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    onClick={() => handleDelete(dept)}
                    title="Delete Department"
                    className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#FDE7E9] hover:border-[#BA0517] hover:text-[#BA0517] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[12px]">
                <Crown size={13} className="text-[#FF9900]" />
                <span className="text-[#706E6B]">Default assignee:</span>
                <span className="font-bold text-[#080707]">
                  {userLabel(dept.defaultAssigneeId?._id || dept.defaultAssigneeId) || (
                    <span className="italic text-[#706E6B]">Unassigned</span>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal
          title={editingDept ? `Edit Department — ${editingDept.name}` : 'New Department'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button
                onClick={() => setShowModal(false)}
                className="h-10 px-4 rounded border border-[#D8DDE6] text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2]"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="h-9 px-5 rounded whitespace-nowrap bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader size={14} className="animate-spin" /> : null}
                {editingDept ? 'Save Changes' : 'Create Department'}
              </button>
            </>
          }
        >
          <FormField label="Department Name *">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Sales"
              className="w-full h-10 px-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all"
            />
          </FormField>

          <FormField label="Default Assignee">
            <div className="relative">
              <select
                value={form.defaultAssigneeId}
                onChange={(e) => setForm({ ...form, defaultAssigneeId: e.target.value })}
                className="w-full appearance-none bg-[#F3F2F2] border border-[#D8DDE6] rounded px-4 h-10 pr-10 text-[13px] text-[#080707] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all cursor-pointer"
              >
                <option value="">Unassigned</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>
                    {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-[11px] text-[#706E6B] mt-1.5 px-3 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded">
              A Workflow's "Assign Department" step routes the lead to this person by default.
            </p>
          </FormField>
        </Modal>
      )}
    </div>
  );
}
